import Phaser from 'phaser';
import { BRAND, HEX } from '../config';
import type { Item, Level } from './levels';
import { ITEMS, isInverter, isBattery, isPanel } from './levels';
import { PanelSprite } from './Panel';

export type HUDState = {
  outputW: number;
  targetW: number;
  storageKWh: number;
  targetKWh: number;
  spent: number;
  budget: number;
  remaining: Record<string, number>;
  hasInverter: boolean;
  batsInSun: number;
  batsCount: number;
};

export class HUD {
  scene: Phaser.Scene;
  level: Level;
  area: Phaser.Geom.Rectangle;
  selectedType: string;
  onSelect: (id: string) => void;
  onSubmit: () => void;
  onReset: () => void;

  private outputValue!: Phaser.GameObjects.Text;
  private outputBarFill!: Phaser.GameObjects.Graphics;
  private outputBarRect = { x: 0, y: 0, w: 0 };
  private storageValue!: Phaser.GameObjects.Text;
  private storageBarFill!: Phaser.GameObjects.Graphics;
  private storageBarRect = { x: 0, y: 0, w: 0 };
  private budgetValue!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private inventoryButtons: {
    type: string;
    container: Phaser.GameObjects.Container;
    countText: Phaser.GameObjects.Text;
    bg: Phaser.GameObjects.Graphics;
  }[] = [];

  constructor(
    scene: Phaser.Scene,
    level: Level,
    area: Phaser.Geom.Rectangle,
    callbacks: {
      onSelect: (id: string) => void;
      onSubmit: () => void;
      onReset: () => void;
    },
  ) {
    this.scene = scene;
    this.level = level;
    this.area = area;
    this.selectedType = level.inventory[0].type;
    this.onSelect = callbacks.onSelect;
    this.onSubmit = callbacks.onSubmit;
    this.onReset = callbacks.onReset;
  }

  build() {
    const { x, y, width } = this.area;
    let cy = y + 10;

    // Title
    this.scene.add
      .text(x + width / 2, cy, this.level.title.replace(/^\d+\s*[—-]\s*/, ''), {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);
    cy += 20;

    // Hint
    this.scene.add
      .text(x + 14, cy, this.level.hint, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.greyDim,
        wordWrap: { width: width - 28 },
        lineSpacing: 1,
      })
      .setOrigin(0, 0);
    cy += 42;

    this.divider(x + 14, cy, width - 28);
    cy += 8;

    // POWER row
    cy = this.statRow(
      x + 14,
      cy,
      width - 28,
      'POWER',
      `${this.level.targetKW.toFixed(1)} kW target`,
      (v) => (this.outputValue = v),
      '0.00 kW',
      HEX.amber,
      (g, rect) => {
        this.outputBarFill = g;
        this.outputBarRect = rect;
      },
    );

    // STORAGE row
    cy = this.statRow(
      x + 14,
      cy,
      width - 28,
      'STORAGE',
      `${this.level.targetKWh} kWh target`,
      (v) => (this.storageValue = v),
      '0 kWh',
      '#57c47a',
      (g, rect) => {
        this.storageBarFill = g;
        this.storageBarRect = rect;
      },
    );

    // BUDGET row (no bar — text only)
    this.scene.add
      .text(x + 14, cy, 'BUDGET', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '9px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);
    this.budgetValue = this.scene.add
      .text(x + width - 14, cy + 1, `$0 / $${this.level.budget}`, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '13px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);
    cy += 18;

    // SYSTEM status (single line, colour conveys severity)
    this.statusText = this.scene.add.text(x + 14, cy, '', {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '10px',
      color: HEX.greyDim,
      fontStyle: 'bold',
      wordWrap: { width: width - 28 },
      lineSpacing: 1,
    });
    cy += 18;

    this.divider(x + 14, cy, width - 28);
    cy += 6;

    // Inventory groups (compact, 28px rows)
    cy = this.buildInventoryGroup('PANELS', x + 14, cy, width - 28, isPanel);
    cy += 4;
    cy = this.buildInventoryGroup('INVERTERS', x + 14, cy, width - 28, isInverter);
    cy += 4;
    cy = this.buildInventoryGroup('BATTERIES', x + 14, cy, width - 28, isBattery);

    this.refreshInventorySelection();

    // Buttons pinned to bottom
    const btnH = 34;
    const btnY = y + this.area.height - btnH - 10;
    this.makeButton(
      'FINISH JOB',
      x + 14,
      btnY,
      (width - 28) / 2 - 4,
      btnH,
      BRAND.red,
      this.onSubmit,
    );
    this.makeButton(
      'RESET',
      x + 14 + (width - 28) / 2 + 4,
      btnY,
      (width - 28) / 2 - 4,
      btnH,
      BRAND.greyDark,
      this.onReset,
    );
  }

  /**
   * Compact stat row: small label + target on the same line, value on the
   * next line, then a thin progress bar. Returns the new cy after this row.
   */
  private statRow(
    x: number,
    cy: number,
    w: number,
    label: string,
    target: string,
    setValue: (t: Phaser.GameObjects.Text) => void,
    initialValue: string,
    valueColor: string,
    setBar: (g: Phaser.GameObjects.Graphics, rect: { x: number; y: number; w: number }) => void,
  ): number {
    this.scene.add
      .text(x, cy, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '9px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);
    this.scene.add
      .text(x + w, cy + 1, target, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '10px',
        color: HEX.greyDim,
      })
      .setOrigin(1, 0);
    cy += 12;
    const v = this.scene.add.text(x, cy, initialValue, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '17px',
      color: valueColor,
      fontStyle: 'bold',
    });
    setValue(v);
    cy += 22;
    const barBg = this.scene.add.graphics();
    barBg.fillStyle(BRAND.inkLine, 1);
    barBg.fillRoundedRect(x, cy, w, 5, 2);
    const fill = this.scene.add.graphics();
    setBar(fill, { x, y: cy, w });
    cy += 12;
    return cy;
  }

  private buildInventoryGroup(
    label: string,
    x: number,
    y: number,
    w: number,
    filter: (it: Item) => boolean,
  ): number {
    const itemsInGroup = this.level.inventory
      .map((it) => ({ inv: it, item: ITEMS[it.type] }))
      .filter(({ item }) => item && filter(item));
    if (itemsInGroup.length === 0) return y;

    let cy = y;
    this.scene.add
      .text(x, cy, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '9px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setLetterSpacing(2);
    cy += 13;

    const itemH = 28;
    const itemGap = 3;
    for (const { inv, item } of itemsInGroup) {
      const btn = this.makeInventoryButton(item, inv.count, x, cy, w, itemH);
      this.inventoryButtons.push(btn);
      cy += itemH + itemGap;
    }
    return cy;
  }

  private divider(x: number, y: number, w: number) {
    const g = this.scene.add.graphics();
    g.fillStyle(0xffffff, 0.07);
    g.fillRect(x, y, w, 1);
  }

  private makeInventoryButton(
    item: Item,
    count: number,
    x: number,
    y: number,
    w: number,
    h: number,
  ) {
    const container = this.scene.add.container(x, y);
    const bg = this.scene.add.graphics();
    container.add(bg);

    // Tiny preview swatch on the left
    const swG = this.scene.add.graphics();
    const swMaxSide = h - 6;
    const longAxis = Math.max(item.width, item.height);
    const cellPx = Math.min(swMaxSide / longAxis, 12);
    const sw = item.width * cellPx;
    const sh = item.height * cellPx;
    const swBox = h - 4;
    PanelSprite.render(
      swG,
      null,
      item,
      4 + (swBox - sw) / 2,
      2 + (swBox - sh) / 2,
      sw,
      sh,
      180,
    );
    container.add(swG);

    // Name + spec on a single line, right-aligned count
    let spec: string;
    if (isPanel(item)) spec = `${item.watts}W · $${item.cost}`;
    else if (isInverter(item)) spec = `${item.capacityKW}kW cap · $${item.cost}`;
    else spec = `${item.storageKWh}kWh · $${item.cost}`;

    const name = this.scene.add.text(swBox + 6, 3, item.name, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '11px',
      color: HEX.white,
      fontStyle: 'bold',
    });
    container.add(name);
    const meta = this.scene.add.text(swBox + 6, 15, spec, {
      fontFamily: 'Albert Sans, sans-serif',
      fontSize: '9px',
      color: HEX.greyDim,
    });
    container.add(meta);

    const countText = this.scene.add
      .text(w - 8, h / 2, `×${count}`, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '12px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);
    container.add(countText);

    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerdown', () => {
      this.selectedType = item.id;
      this.onSelect(item.id);
      this.refreshInventorySelection();
    });

    return { type: item.id, container, countText, bg };
  }

  private refreshInventorySelection() {
    const w = this.area.width - 28;
    const h = 28;
    for (const item of this.inventoryButtons) {
      item.bg.clear();
      const isSel = item.type === this.selectedType;
      item.bg.fillStyle(isSel ? 0x4a2228 : BRAND.inkLine, isSel ? 1 : 0.6);
      item.bg.fillRoundedRect(0, 0, w, h, 4);
      if (isSel) {
        item.bg.lineStyle(1.5, BRAND.red, 1);
        item.bg.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, 4);
      } else {
        item.bg.lineStyle(1, 0xffffff, 0.06);
        item.bg.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, 4);
      }
    }
  }

  private makeButton(
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const bg = this.scene.add.graphics();
    const paint = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(0x000000, 0.3);
      bg.fillRoundedRect(0, 2, w, h, 6);
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, 6);
      if (hover) {
        bg.lineStyle(2, BRAND.amber, 1);
        bg.strokeRoundedRect(0, 0, w, h, 6);
      }
    };
    paint(false);
    c.add(bg);
    const txt = this.scene.add
      .text(w / 2, h / 2, label, {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '13px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(1);
    c.add(txt);
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerover', () => paint(true));
    bg.on('pointerout', () => paint(false));
    bg.on('pointerdown', onClick);
    return c;
  }

  update(state: HUDState) {
    // Power
    this.outputValue.setText(`${(state.outputW / 1000).toFixed(2)} kW`);
    const pRatio = state.targetW > 0 ? Math.min(1, state.outputW / state.targetW) : 1;
    this.outputBarFill.clear();
    const pColor = state.outputW >= state.targetW ? BRAND.amber : BRAND.orange;
    this.outputBarFill.fillStyle(pColor, 1);
    this.outputBarFill.fillRoundedRect(
      this.outputBarRect.x,
      this.outputBarRect.y,
      Math.max(2, this.outputBarRect.w * pRatio),
      5,
      2,
    );

    // Storage
    this.storageValue.setText(`${state.storageKWh} kWh`);
    const sRatio = state.targetKWh > 0 ? Math.min(1, state.storageKWh / state.targetKWh) : 1;
    this.storageBarFill.clear();
    const sColor = state.storageKWh >= state.targetKWh ? 0x57c47a : 0xe2a93a;
    this.storageBarFill.fillStyle(sColor, 1);
    this.storageBarFill.fillRoundedRect(
      this.storageBarRect.x,
      this.storageBarRect.y,
      Math.max(2, this.storageBarRect.w * sRatio),
      5,
      2,
    );

    // Budget — going over no longer fails the level, just costs stars.
    const overBudget = state.spent > state.budget;
    const overBy = state.spent - state.budget;
    this.budgetValue.setText(
      overBudget
        ? `$${state.spent} / $${state.budget}  (+$${overBy})`
        : `$${state.spent} / $${state.budget}`,
    );
    this.budgetValue.setColor(overBudget ? HEX.amber : HEX.white);

    // Status — single concise line
    let status: string;
    let statusColor: string;
    if (!state.hasInverter) {
      status = '⚠ NO INVERTER — output is 0';
      statusColor = HEX.red;
    } else if (state.batsInSun > 0) {
      status = `⚠ ${state.batsInSun} ${state.batsInSun === 1 ? 'battery' : 'batteries'} in sun — output penalty`;
      statusColor = HEX.red;
    } else {
      status = 'SYSTEM: ✓ wired';
      statusColor = HEX.amberSoft;
    }
    this.statusText.setText(status);
    this.statusText.setColor(statusColor);

    for (const item of this.inventoryButtons) {
      const remaining = state.remaining[item.type] ?? 0;
      item.countText.setText(`×${remaining}`);
      item.countText.setColor(remaining === 0 ? '#5a6271' : HEX.amber);
    }
  }
}
